import {
  ImageAnalysisQuery,
  ImageAnalysisResult,
  ImageAnalysisResultContent,
  LISTING_BY_ADDRESS_API,
  assert,
  checkLength,
  checkMinLength,
  clamp,
  isDefined,
  schAddress,
} from '@aloft/toolkit-shared';
import {
  Block,
  Button,
  Flex,
  SimpleTextArea,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Typography,
} from '@aloft/toolkit-sui';
import { UseMutationResult } from '@tanstack/react-query';

import _ from 'lodash';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AddressInput } from '../../components/AddressInput/AddressInput.tsx';
import { usePostImageAnalysis } from '../../hooks/api/schema-hooks/usePostImageAnalysis.ts';
import { useGetListingByAddress } from '../../hooks/api/toolkitComponents.ts';
import { makeReactHotToast } from '../../hooks/useToaster/makeReactHotToast.tsx';
import { MainLayout } from '../../layout/MainLayout.tsx';

type UsePostImageAnalysis<T = ImageAnalysisQuery> = UseMutationResult<
  ImageAnalysisResult,
  unknown,
  { body: T }
>;

const ADDRESS_LOOKUP_TOAST = {
  id: 'image-analysis-demo-address-lookup',
  durationInMs: 5000,
} as const;

const ANALYSIS_TOAST = {
  id: 'image-analysis-demo-analysis',
  durationInMs: 5000,
} as const;

export const ImageAnalysisDemoRouteElement = () => {
  const [inputURLs, setInputURLs] = useState<string>('');

  const form = useForm();

  const {
    data: result,
    variables,
    isLoading,
    mutate,
  } = usePostImageAnalysis({
    onError: (e) =>
      makeReactHotToast({
        ...ANALYSIS_TOAST,
        type: 'error',
        title: 'Analysis failed',
        body: (e?.payload as any).message || 'Try again shortly',
      }),
  }) /* mild openapi weirdness that we have to do this twice: */ as UsePostImageAnalysis<unknown> as UsePostImageAnalysis;

  const { mutate: mutateGetListingByAddress, isLoading: isAddressLoading } = useGetListingByAddress(
    {
      onError: (e) => {
        makeReactHotToast({
          ...ADDRESS_LOOKUP_TOAST,
          title: 'Could not find listing',
          body: (e?.payload as any).message || undefined,
          type: 'error',
        });
      },
    },
  );

  function runAnalysis(): void {
    const imageURLs = inputURLs
      .split('\n')
      .map((x) => x.trim() || undefined)
      .filter(isDefined);
    mutate({ body: { listings: [{ imageURLs }] } });
  }

  function runGetListingByAddress(): void {
    const address = schAddress.parse(form.getValues().address ?? {});
    function onSuccess(data: unknown): void {
      const res = LISTING_BY_ADDRESS_API.POST.RESPONSE.parse(data);
      try {
        const { Media } = checkLength(1, res.results, 'No listings in dataset')[0];
        const images = checkMinLength(1, Media ?? [], 'No images in listing');
        setInputURLs(images.map((x) => x.MediaURL).join('\n'));
        makeReactHotToast({
          ...ADDRESS_LOOKUP_TOAST,
          type: 'success',
          title: 'Images prefilled from address',
          body: 'Click "Analyze" to run analysis',
        });
      } catch (e) {
        assert(e instanceof Error);
        makeReactHotToast({
          ...ADDRESS_LOOKUP_TOAST,
          type: 'error',
          title: 'Could not prefill from listing',
          body: e.message,
        });
      }
    }
    mutateGetListingByAddress({ body: address }, { onSuccess });
  }

  const urls = variables?.body?.listings?.[0]?.imageURLs ?? [];

  return (
    <MainLayout title="Image Analysis" showNav={false}>
      <Flex alignItems="stretch" height="100%" overflow="hidden">
        <Block
          padding={4}
          maxWidth="40rem"
          boxSizing="content-box"
          flexBasis="40rem"
          flexGrow={0}
          flexShrink={1}
          overflowY="auto"
        >
          <Block margin="0 0 1rem 0">
            <FormProvider {...form}>
              <AddressInput addressLabel="Address lookup" isRequired={false} />
            </FormProvider>
            <Button
              size="sm"
              onClick={() => runGetListingByAddress()}
              isLoading={isAddressLoading}
              text="Fill image URLs by address"
            />
          </Block>
          <Block margin="0 0 1rem 0">
            <Typography variant="body1">Enter a list of image URLs, one per line.</Typography>
            <SimpleTextArea
              value={inputURLs}
              onChange={(e) => setInputURLs(e.target.value)}
              id="urls"
              size="lg"
              resize="vertical"
            />
            <Button onClick={() => runAnalysis()} disabled={isLoading} text="Analyze!" size="sm" />
          </Block>
          <Block margin="1rem 0">
            {isLoading && <Typography variant="h3">🔬 Analyzing your images... (~30s)</Typography>}
            {result && <Result result={result} />}
          </Block>
        </Block>
        <Flex
          flexBasis="1px"
          flexGrow={1}
          flexShrink={1}
          backgroundColor="surface2"
          padding={4}
          flexDirection="row"
          flexWrap="wrap"
          gap={1}
          overflowY="auto"
          alignContent="flex-start"
        >
          {urls.map((url) => (
            <Block flexBasis="30rem" flexGrow={1} flexShrink={0} key={url}>
              <img src={url} />
            </Block>
          ))}
        </Flex>
      </Flex>
    </MainLayout>
  );
};

const booleanKeys: (keyof ImageAnalysisResultContent)[] = [
  'has_barn',
  'has_carport',
  'has_basement',
  'has_deck',
  'has_covered_porch',
  'has_enclosed_patio',
  'has_accessory_dwelling_unit',
  'has_pool',
  'has_shed',
  'has_workshop',
  'has_fireplace',
];

const booleanKeyLabels: Partial<Record<keyof ImageAnalysisResultContent, string>> = {
  has_barn: 'Barn',
  has_carport: 'Carport',
  has_basement: 'Basement',
  has_deck: 'Deck',
  has_covered_porch: 'Covered Porch',
  has_enclosed_patio: 'Enclosed Patio',
  has_accessory_dwelling_unit: 'Accessory Dwelling Unit',
  has_pool: 'Pool',
  has_shed: 'Shed',
  has_workshop: 'Workshop',
  has_fireplace: 'Fireplace',
};

const classLabels = [
  'Luxury',
  'Semi-Luxury',
  'Best Std.',
  'Good Std.',
  'Average Std.',
  'Minimum Std.',
];

const Result = (props: { result: ImageAnalysisResult }) => {
  const {
    result,
    result: {
      content,
      content: { uad_condition_rating, uad_condition_commentary },
    },
  } = props;
  const [showJSON, setShowJSON] = useState<boolean>(false);

  return (
    <>
      <Typography variant="h3">Output</Typography>
      <Table variant="grid">
        <Thead>
          <Tr>
            <Th />
            {classLabels.map((s, i) => (
              <Th key={i}>
                <Typography variant="h5">Class {(i + 1).toFixed(0)}</Typography>
                <Typography variant="h5">{s}</Typography>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          <ResultRow result={result} label="Floor Structure" prefix="floor_structure" />
          <ResultRow
            result={result}
            label="Wall Framing and Exterior Finish"
            prefix="wall_framing_and_exterior_finish"
          />
          <ResultRow result={result} label="Roof" prefix="roof" />
          <ResultRow result={result} label="Floor Finish" prefix="floor_finish" />
          <ResultRow
            result={result}
            label="Interior Wall and Ceiling Finish"
            prefix="interior_wall_and_ceiling_finish"
          />
          <ResultRow result={result} label="Interior Detail" prefix="interior_detail" />
          <ResultRow result={result} label="Bath Detail" prefix="bath_detail" />
          <ResultRow result={result} label="Kitchen Detail" prefix="kitchen_detail" />
          <ResultRow result={result} label="Plumbing Detail" prefix="plumbing_detail" />
          <ResultRow result={result} label="Special Features" prefix="special_features" />
          <ResultRow result={result} label="Electrical System" prefix="electrical_system" />
          <ResultRow result={result} label="Overall" prefix="overall" />
        </Tbody>
      </Table>
      <Block margin="1rem 0">
        <Typography variant="h4">Condition</Typography>
        <Typography variant="body2">
          {uad_condition_rating ?? 'n/a'}: {uad_condition_commentary ?? 'n/a'}
        </Typography>
      </Block>
      <Block margin="1rem 0">
        <Typography variant="h4">Has</Typography>
        <ul>
          {booleanKeys
            .filter((x) => content[x] === true)
            .map((k) => (
              <li key={k}>
                <Typography variant="body2">{booleanKeyLabels[k] ?? k}</Typography>
              </li>
            ))}
        </ul>
      </Block>
      <Block margin="1rem 0">
        <Typography variant="h4">Does not have</Typography>
        <ul>
          {booleanKeys
            .filter((x) => content[x] === false)
            .map((k) => (
              <li key={k}>
                <Typography variant="body2">{booleanKeyLabels[k] ?? k}</Typography>
              </li>
            ))}
        </ul>
      </Block>
      <Block margin="1rem 0">
        {!showJSON && (
          <Button variant="inline" size="sm" text="Show JSON" onClick={() => setShowJSON(true)} />
        )}
        {showJSON && (
          <div
            style={{
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
              fontFamily: 'monospace',
              fontSize: '10px',
            }}
          >
            {JSON.stringify(result, undefined, 2)}
          </div>
        )}
      </Block>
    </>
  );
};

const ResultRow = (props: { result: ImageAnalysisResult; label: string; prefix: string }) => {
  const {
    result: { content },
    label,
    prefix,
  } = props;

  const classKey = `${prefix}_class`;
  const commentaryKey = `${prefix}_commentary`;

  const { [classKey]: classValue } = z.object({ [classKey]: z.number().optional() }).parse(content);
  const { [commentaryKey]: commentaryValue } = z
    .object({ [commentaryKey]: z.string().optional() })
    .parse(content);

  const column = (typeof classValue === 'number' ? clamp(Math.floor(classValue), 1, 6) : 0) - 1;
  const cellContent = [classValue?.toString(), commentaryValue].filter(isDefined).join(': ');

  return (
    <Tr>
      <Td>
        <Typography variant="h5">{label}</Typography>
      </Td>
      {_.range(0, 6).map((i) => (
        <Td key={i}>{i === column && <Typography variant="body2">{cellContent}</Typography>}</Td>
      ))}
    </Tr>
  );
};
